import React, { useEffect, useRef, useState, useCallback, useMemo } from 'react';
import { 
  Box, 
  Card, 
  CardContent, 
  Typography, 
  Chip, 
  List, 
  ListItem, 
  ListItemText,
  Alert,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Skeleton,
  Collapse,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper
} from '@mui/material';
import { ExpandMore, ExpandLess, KeyboardArrowDown, KeyboardArrowUp } from '@mui/icons-material';
import { getBusinessCoordinatesWithFallback, logCoordinateDebug } from '../../utils/coordinateUtils';

const LocationMap = ({ businessLocation, keywords = [] }) => {
  const mapRef = useRef(null);
  const [map, setMap] = useState(null);
  const [selectedKeyword, setSelectedKeyword] = useState('');
  const [mapError, setMapError] = useState(false);
  const [mapLoading, setMapLoading] = useState(true);
  const [detailsExpanded, setDetailsExpanded] = useState(true);
  const [competitorsExpanded, setCompetitorsExpanded] = useState(true);
  const [expandedRows, setExpandedRows] = useState({});
  const markersRef = useRef([]);

  // Memoized keyword data für bessere Performance
  const selectedKeywordData = useMemo(() => {
    return keywords.find(k => k.keyword === selectedKeyword);
  }, [keywords, selectedKeyword]);

  // Memoized competitor data for table
  const competitorTableData = useMemo(() => {
    if (!selectedKeywordData?.testLocations) return [];
    
    return selectedKeywordData.testLocations.map((location, index) => ({
      id: index,
      locationName: location.locationName,
      isCenter: location.isCenter || location.locationName === 'Firmenstandort',
      yourRank: location.rank,
      competitors: location.competitors || [],
      coordinates: location.lat && location.lng ? `${location.lat.toFixed(4)}, ${location.lng.toFixed(4)}` : 'N/A',
      address: location.address || 'Adresse nicht verfügbar'
    }));
  }, [selectedKeywordData]);

  // Toggle row expansion
  const handleRowExpand = (rowId) => {
    setExpandedRows(prev => ({
      ...prev,
      [rowId]: !prev[rowId]
    }));
  };

  // Initialisiere Karte nur einmal
  useEffect(() => {
    let mounted = true;
    
    const initMap = async () => {
      if (!mounted) return;
      
      try {
        setMapLoading(true);
        
        // Cleanup existing map first
        if (map) {
          try {
            map.remove();
          } catch (e) {
            console.warn('Error removing map:', e);
          }
          setMap(null);
        }

        // Clear the map container completely
        if (mapRef.current) {
          // Force cleanup of any existing Leaflet instances
          mapRef.current.innerHTML = '';
          if (mapRef.current._leaflet_id) {
            delete mapRef.current._leaflet_id;
          }
          // Remove any remaining Leaflet classes
          mapRef.current.className = mapRef.current.className.replace(/leaflet-\S+/g, '');
        }

        // Small delay for cleanup
        await new Promise(resolve => setTimeout(resolve, 100));
        
        if (!mounted) return;

        // Initialize map
        if (typeof window !== 'undefined' && window.L) {
          initializeMap();
        } else {
          loadLeaflet();
        }
      } catch (error) {
        console.error('Error initializing map:', error);
        if (mounted) {
          setMapError(true);
          setMapLoading(false);
        }
      }
    };

    initMap();

    return () => {
      mounted = false;
      if (map) {
        try {
          map.remove();
        } catch (e) {
          console.warn('Error removing map in cleanup:', e);
        }
      }
      if (mapRef.current) {
        mapRef.current.innerHTML = '';
        if (mapRef.current._leaflet_id) {
          delete mapRef.current._leaflet_id;
        }
        mapRef.current.className = mapRef.current.className.replace(/leaflet-\S+/g, '');
      }
    };
  }, [businessLocation]); // Re-run when businessLocation changes

  // Automatisches Keyword-Selection bei Keywords-Änderung
  useEffect(() => {
    if (keywords.length > 0 && !selectedKeyword) {
      setSelectedKeyword(keywords[0].keyword);
    } else if (keywords.length === 0) {
      setSelectedKeyword('');
    } else if (selectedKeyword && !keywords.find(k => k.keyword === selectedKeyword)) {
      // Wenn das ausgewählte Keyword nicht mehr existiert, wähle das erste verfügbare
      setSelectedKeyword(keywords[0]?.keyword || '');
    }
  }, [keywords, selectedKeyword]);

  // Optimierte Marker-Updates nur bei Keyword-Wechsel
  useEffect(() => {
    if (map && selectedKeyword) {
      updateMapMarkers();
    }
  }, [selectedKeyword, map]);

  const loadLeaflet = () => {
    // CSS laden
    if (!document.querySelector('link[href*="leaflet.css"]')) {
      const link = document.createElement('link');
      link.rel = 'stylesheet';
      link.href = 'https://unpkg.com/leaflet@1.9.4/dist/leaflet.css';
      document.head.appendChild(link);
    }

    // JavaScript laden
    if (!window.L) {
      const script = document.createElement('script');
      script.src = 'https://unpkg.com/leaflet@1.9.4/dist/leaflet.js';
      script.onload = initializeMap;
      script.onerror = () => setMapError(true);
      document.head.appendChild(script);
    }
  };

  const initializeMap = useCallback(() => {
    try {
      if (!mapRef.current || !window.L) {
        console.warn('🚫 Cannot initialize map: mapRef or Leaflet not available');
        return;
      }

      // Only initialize if we have some location data
      if (!businessLocation && keywords.length === 0) {
        console.warn('🚫 Cannot initialize map: No business location or keywords available');
        setMapLoading(false);
        return;
      }

      console.log('🚀 Initializing map with businessLocation:', businessLocation);

      // Check if container already has a map
      if (mapRef.current._leaflet_id) {
        console.warn('Map container already initialized, clearing...');
        mapRef.current.innerHTML = '';
        delete mapRef.current._leaflet_id;
        mapRef.current.className = mapRef.current.className.replace(/leaflet-\S+/g, '');
      }

      // Double-check the container is empty
      if (mapRef.current.children.length > 0) {
        mapRef.current.innerHTML = '';
      }

      // FIXED: Use ACTUAL business location coordinates from Google Business Profile
      const coordinates = getBusinessCoordinatesWithFallback(businessLocation, keywords);
      const businessLat = coordinates.lat;
      const businessLng = coordinates.lng;
      
      // Log coordinate usage
      logCoordinateDebug(businessLocation, keywords, 'LocationMap');

      console.log(`🗺️ Final coordinates for map center: ${businessLat}, ${businessLng}`);

      const mapInstance = window.L.map(mapRef.current, {
        zoomControl: true,
        attributionControl: true,
        preferCanvas: true // Bessere Performance für viele Marker
      }).setView([businessLat, businessLng], 12); // Zoom 12 for better detail

      // OpenStreetMap Tiles mit optimierten Einstellungen
      window.L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
        attribution: '© OpenStreetMap contributors',
        maxZoom: 18,
        tileSize: 256,
        zoomOffset: 0
      }).addTo(mapInstance);

      setMap(mapInstance);
      setMapLoading(false);

    } catch (error) {
      console.error('Error initializing map:', error);
      setMapError(true);
      setMapLoading(false);
    }
  }, [businessLocation]);

  const createRankMarker = (rank, hasCompetitors = false) => {
    // FIXED: Handle N/A, null, undefined as worst ranking (20)
    const effectiveRank = rank && rank !== 'N/A' && !isNaN(rank) ? rank : 20;
    const displayRank = rank && rank !== 'N/A' && !isNaN(rank) ? rank : '20';
    
    let backgroundColor = '#f44336'; // Rot für schlechte Rankings (Default)
    
    if (effectiveRank <= 3) backgroundColor = '#4caf50'; // Grün für Top 3
    else if (effectiveRank <= 10) backgroundColor = '#ff9800'; // Orange für Top 10
    // Alles andere bleibt rot (#f44336)

    // DEBUGGING: Add console log to track rank values
    console.log(`🗺️ Creating marker: rank=${rank}, effectiveRank=${effectiveRank}, displayRank=${displayRank}`);

    return window.L.divIcon({
      html: `
        <div style="
          background: ${backgroundColor};
          border: 3px solid white;
          border-radius: 50%;
          width: 46px;
          height: 46px;
          display: flex;
          align-items: center;
          justify-content: center;
          box-shadow: 0 4px 6px rgba(0,0,0,0.15);
          font-weight: bold;
          color: white;
          font-size: 18px;
          cursor: pointer;
        ">
          ${displayRank}
        </div>
      `,
      className: 'custom-rank-marker',
      iconSize: [46, 46],
      iconAnchor: [23, 23]
    });
  };

  // Optimierte Marker-Updates mit Caching
  const updateMapMarkers = useCallback(() => {
    if (!map || !window.L || !selectedKeyword) return;

    // Entferne nur vorherige Marker (Performance-Optimierung)
    markersRef.current.forEach(marker => {
      try {
        map.removeLayer(marker);
      } catch (e) {
        // Marker bereits entfernt
      }
    });
    markersRef.current = [];

    const keywordData = keywords.find(k => k.keyword === selectedKeyword);
    
    if (!keywordData || !keywordData.testLocations) {
      return;
    }

    // Füge alle 18 Ranking-Marker hinzu
    const newMarkers = [];
    
    keywordData.testLocations.forEach((location, index) => {
      if (location.lat && location.lng) {
        const marker = window.L.marker([location.lat, location.lng], {
          icon: createRankMarker(location.rank, location.competitors?.length > 0),
          riseOnHover: true
        });

        // Popup mit optimiertem Content
        const isCenter = location.isCenter || location.locationName === 'Firmenstandort';
        const popupTitle = isCenter ? 
          `${businessLocation?.name || 'Ihr Unternehmen'} (Firmenstandort)` : 
          location.locationName;
        
        const competitorsList = location.competitors?.slice(0, 5).map(comp => 
          `<li style="margin: 2px 0;">${comp.name} <span style="color: #666;">(Rang ${comp.rank})</span></li>`
        ).join('') || '<li>Keine Wettbewerber-Daten</li>';

        marker.bindPopup(`
          <div style="max-width: 280px; font-family: Arial, sans-serif;">
            <h4 style="margin: 0 0 8px 0; color: #333;">${popupTitle}</h4>
            ${isCenter ? '<p style="color: #1976d2; font-weight: bold; margin: 4px 0;">🏢 Ihr Firmenstandort</p>' : ''}
            <div style="background: #f5f5f5; padding: 8px; border-radius: 4px; margin: 8px 0;">
              <strong>Rang für "${selectedKeyword}":</strong> 
              <span style="color: ${(() => {
                const effectiveRank = location.rank && location.rank !== 'N/A' && !isNaN(location.rank) ? location.rank : 20;
                return effectiveRank <= 3 ? '#4caf50' : effectiveRank <= 10 ? '#ff9800' : '#f44336';
              })()}; font-weight: bold;">
                ${location.rank && location.rank !== 'N/A' && !isNaN(location.rank) ? location.rank : 'Nicht gefunden (20)'}
              </span>
            </div>
            <p style="margin: 8px 0 4px 0; font-weight: bold; color: #555;">Top Wettbewerber:</p>
            <ul style="margin: 4px 0; padding-left: 16px; font-size: 0.9em;">
              ${competitorsList}
            </ul>
          </div>
        `, {
          maxWidth: 300,
          className: 'custom-popup'
        });

        marker.addTo(map);
        newMarkers.push(marker);
      }
    });

    markersRef.current = newMarkers;

    // Optimierte Zoom-Anpassung nur bei Bedarf
    if (newMarkers.length > 0) {
      const group = new window.L.featureGroup(newMarkers);
      map.fitBounds(group.getBounds().pad(0.02));
    }
  }, [map, selectedKeyword, keywords, businessLocation]);

  if (mapError) {
    return (
      <Alert severity="error">
        Karte konnte nicht geladen werden. Prüfen Sie Ihre Internetverbindung.
      </Alert>
    );
  }

  if (keywords.length === 0) {
    return (
      <Alert severity="info">
        Keine Keyword-Daten verfügbar. Analysieren Sie erst ein Keyword, um die Karte zu sehen.
      </Alert>
    );
  }

  return (
    <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>

      {/* Map Container */}
      <Box
        sx={{
          height: '400px',
          borderRadius: 1,
          border: '1px solid #e0e0e0',
          position: 'relative',
          overflow: 'hidden',
          flexShrink: 0
        }}
      >
        {mapLoading && (
          <Box
            sx={{
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              backgroundColor: 'rgba(255, 255, 255, 0.8)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              zIndex: 1000
            }}
          >
            <Box textAlign="center">
              <Skeleton variant="rectangular" width="100%" height={60} sx={{ mb: 2 }} />
              <Typography variant="body2" color="textSecondary">
                Karte wird geladen...
              </Typography>
            </Box>
          </Box>
        )}
        
        <Box
          ref={mapRef}
          sx={{
            height: '100%',
            width: '100%',
            '& .leaflet-popup-content-wrapper': {
              borderRadius: '8px',
              boxShadow: '0 4px 12px rgba(0,0,0,0.15)'
            },
            '& .custom-popup .leaflet-popup-content': {
              margin: '8px',
              lineHeight: '1.4'
            }
          }}
        />
      </Box>

      {/* Scrollable Details Section */}
      <Box sx={{ flex: 1, overflow: 'auto', mt: 1.5 }}>

        {/* Kollapsible Ranking Details */}
        {selectedKeywordData && (
          <Box>
            {/* Ranking Details Section */}
            <Box 
              display="flex" 
              alignItems="center" 
              justifyContent="space-between"
              sx={{ 
                p: 1.5, 
                border: '1px solid #e0e0e0', 
                borderRadius: 1,
                backgroundColor: '#f9f9f9',
                cursor: 'pointer'
              }}
              onClick={() => setDetailsExpanded(!detailsExpanded)}
            >
              <Box>
                <Typography variant="subtitle2" fontWeight={600} sx={{ mb: 0.5 }}>
                  Ranking-Details
                </Typography>
                <Typography variant="caption" color="textSecondary">
                  Durchschnittsrang {selectedKeywordData.averageRank || 'N/A'} • {selectedKeywordData.testLocations?.length || 0} Messpunkte
                </Typography>
              </Box>
              <IconButton size="small">
                {detailsExpanded ? <ExpandLess /> : <ExpandMore />}
              </IconButton>
            </Box>
            
            <Collapse in={detailsExpanded}>
              <Box sx={{ p: 1.5, border: '1px solid #e0e0e0', borderTop: 'none', borderRadius: '0 0 4px 4px' }}>
                {/* Erklärung */}
                <Typography variant="caption" color="textSecondary" sx={{ mb: 1, display: 'block' }}>
                  📍 Ihr Ranking wird an 18 verschiedenen Standorten gemessen: 1 Zentrum (Ihr Standort) + 6 innerer Kreis (7.5km) + 12 äußerer Kreis (15km)
                </Typography>
                
                <Box display="flex" gap={2} flexWrap="wrap" mb={1}>
                  <Box>
                    <Typography variant="caption" color="textSecondary" display="block">
                      Bester Rang
                    </Typography>
                    <Typography variant="body2" fontWeight={600} color="success.main">
                      {selectedKeywordData.bestRank || 'N/A'}
                    </Typography>
                  </Box>
                  <Box>
                    <Typography variant="caption" color="textSecondary" display="block">
                      Schlechtester Rang
                    </Typography>
                    <Typography variant="body2" fontWeight={600} color="error.main">
                      {selectedKeywordData.worstRank || '20'}
                    </Typography>
                  </Box>
                </Box>
                
                {selectedKeywordData.lastAnalyzed && (
                  <Box display="flex" alignItems="center" gap={1}>
                    <Typography variant="caption" color="textSecondary">
                      Datenstand: {new Date(selectedKeywordData.lastAnalyzed?.$date || selectedKeywordData.lastAnalyzed).toLocaleDateString('de-DE', {
                        day: '2-digit',
                        month: '2-digit',
                        year: 'numeric',
                        hour: '2-digit',
                        minute: '2-digit'
                      })}
                    </Typography>
                    <Chip
                      label="Nicht live"
                      size="small"
                      variant="outlined"
                      color="warning"
                      sx={{ fontSize: '0.65rem', height: 16 }}
                    />
                  </Box>
                )}
              </Box>
            </Collapse>

            {/* Competitor Details Section */}
            <Box mt={1.5}>
              <Box 
                display="flex" 
                alignItems="center" 
                justifyContent="space-between"
                sx={{ 
                  p: 1.5, 
                  border: '1px solid #e0e0e0', 
                  borderRadius: 1,
                  backgroundColor: '#f9f9f9',
                  cursor: 'pointer'
                }}
                onClick={() => setCompetitorsExpanded(!competitorsExpanded)}
              >
                <Box>
                  <Typography variant="subtitle2" fontWeight={600} sx={{ mb: 0.5 }}>
                    Wettbewerber-Details
                  </Typography>
                  <Typography variant="caption" color="textSecondary">
                    Detaillierte Wettbewerber-Analyse für alle 18 Standorte
                  </Typography>
                </Box>
                <IconButton size="small">
                  {competitorsExpanded ? <ExpandLess /> : <ExpandMore />}
                </IconButton>
              </Box>
              
              <Collapse in={competitorsExpanded}>
                <Box sx={{ border: '1px solid #e0e0e0', borderTop: 'none', borderRadius: '0 0 4px 4px' }}>
                  {competitorTableData.length > 0 ? (
                    <TableContainer 
                      component={Paper} 
                      variant="outlined" 
                      sx={{ 
                        maxHeight: 400,
                        overflow: 'auto',
                        borderRadius: '0 0 4px 4px',
                        border: 'none'
                      }}
                    >
                      <Table size="small" stickyHeader>
                        <TableHead>
                          <TableRow>
                            <TableCell sx={{ fontWeight: 'bold', backgroundColor: '#f5f5f5', position: 'sticky', top: 0, width: '40px' }}></TableCell>
                            <TableCell sx={{ fontWeight: 'bold', backgroundColor: '#f5f5f5', position: 'sticky', top: 0 }}>Standort</TableCell>
                            <TableCell sx={{ fontWeight: 'bold', backgroundColor: '#f5f5f5', position: 'sticky', top: 0 }}>Ihr Rang</TableCell>
                            <TableCell sx={{ fontWeight: 'bold', backgroundColor: '#f5f5f5', position: 'sticky', top: 0 }}>Top Wettbewerber</TableCell>
                            <TableCell sx={{ fontWeight: 'bold', backgroundColor: '#f5f5f5', position: 'sticky', top: 0 }}>Anzahl</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {competitorTableData.map((row) => (
                            <React.Fragment key={row.id}>
                              <TableRow 
                                sx={{ 
                                  backgroundColor: row.isCenter ? '#e8f4fd' : 'inherit',
                                  '&:hover': { backgroundColor: row.isCenter ? '#d0e9fb' : '#f5f5f5' }
                                }}
                              >
                                <TableCell>
                                  <IconButton
                                    aria-label="expand row"
                                    size="small"
                                    onClick={() => handleRowExpand(row.id)}
                                  >
                                    {expandedRows[row.id] ? <KeyboardArrowUp /> : <KeyboardArrowDown />}
                                  </IconButton>
                                </TableCell>
                                <TableCell>
                                  <Box display="flex" alignItems="center" gap={1}>
                                    {row.isCenter && (
                                      <Chip 
                                        label="Zentrum" 
                                        size="small" 
                                        color="primary" 
                                        variant="filled"
                                        sx={{ fontSize: '0.6rem', height: 20 }}
                                      />
                                    )}
                                    <Box>
                                      <Typography variant="body2" sx={{ fontSize: '0.8rem', fontWeight: 500 }}>
                                        {row.locationName}
                                      </Typography>
                                      <Typography variant="caption" color="textSecondary" sx={{ fontSize: '0.7rem' }}>
                                        {row.coordinates}
                                      </Typography>
                                    </Box>
                                  </Box>
                                </TableCell>
                                <TableCell>
                                  <Chip 
  label={row.yourRank || 'N/A'} 
  size="small"
  color={
    
    (row.yourRank <= 3 && row.yourRank >=1) ? 'success' : 
    (row.yourRank <= 10 && row.yourRank >=4) ? 'warning' : 
    'error'
  }
  variant="filled"
  sx={{ fontSize: '0.7rem' }}
/>
                                </TableCell>
                                <TableCell>
                                  {row.competitors.length > 0 ? (
                                    <Box>
                                      {row.competitors.slice(0, 2).map((competitor, compIndex) => (
                                        <Typography key={compIndex} variant="body2" sx={{ fontSize: '0.75rem', mb: 0.5 }}>
                                          {competitor.name} <span style={{ color: '#666' }}>(Rang {competitor.rank})</span>
                                        </Typography>
                                      ))}
                                      {row.competitors.length > 2 && (
                                        <Typography variant="caption" color="textSecondary">
                                          + {row.competitors.length - 2} weitere
                                        </Typography>
                                      )}
                                    </Box>
                                  ) : (
                                    <Typography variant="body2" color="textSecondary" sx={{ fontSize: '0.75rem' }}>
                                      Keine Wettbewerber
                                    </Typography>
                                  )}
                                </TableCell>
                                <TableCell>
                                  <Chip 
                                    label={row.competitors.length} 
                                    size="small"
                                    variant="outlined"
                                    color={row.competitors.length > 0 ? 'primary' : 'default'}
                                    sx={{ fontSize: '0.7rem' }}
                                  />
                                </TableCell>
                              </TableRow>
                              
                              {/* Expanded Competitor Details */}
                              <TableRow>
                                <TableCell style={{ paddingBottom: 0, paddingTop: 0 }} colSpan={6}>
                                  <Collapse in={expandedRows[row.id]} timeout="auto" unmountOnExit>
                                    <Box sx={{ margin: 1, p: 2, backgroundColor: '#fafafa', borderRadius: 1 }}>
                                      <Typography variant="subtitle2" gutterBottom sx={{ fontSize: '0.8rem' }}>
                                        📍 {row.locationName} {row.isCenter && '(Ihr Firmenstandort)'}
                                      </Typography>
                                      <Typography variant="caption" color="textSecondary" display="block" sx={{ mb: 1, fontSize: '0.7rem' }}>
                                        {row.address} • Koordinaten: {row.coordinates}
                                      </Typography>
                                      
                                      {row.competitors.length > 0 ? (
                                        <Table size="small" sx={{ backgroundColor: 'white' }}>
                                          <TableHead>
                                            <TableRow>
                                              <TableCell sx={{ fontWeight: 'bold', fontSize: '0.7rem' }}>Wettbewerber</TableCell>
                                              <TableCell sx={{ fontWeight: 'bold', fontSize: '0.7rem' }}>Rang</TableCell>
                                              <TableCell sx={{ fontWeight: 'bold', fontSize: '0.7rem' }}>
                                                PlaceId
                                              </TableCell>
                                              <TableCell sx={{ fontWeight: 'bold', fontSize: '0.7rem' }}>Bewertung</TableCell>
                                              <TableCell sx={{ fontWeight: 'bold', fontSize: '0.7rem' }}>Bewertungen</TableCell>
                                            </TableRow>
                                          </TableHead>
                                          <TableBody>
                                            {row.competitors.map((competitor, index) => {  
                                            
                                            
                                            return (
                                             
                                              <TableRow key={index}>
                                                <TableCell sx={{ fontSize: '0.75rem' }}>
                                                  <Typography variant="body2" sx={{ fontSize: '0.75rem', fontWeight: 500 }}>
                                                    {competitor.name}
                                                  </Typography>
                                                  {competitor.address && (
                                                    <Typography variant="caption" color="textSecondary" sx={{ fontSize: '0.65rem' }}>
                                                      {competitor.address}
                                                    </Typography>
                                                  )}
                                                </TableCell>
                                                <TableCell sx={{ fontSize: '0.75rem' }}>
                                                  <Chip 
                                                    label={competitor.rank || 'N/A'} 
                                                    size="small"
                                                    color={
                                                      competitor.rank <= 3 ? 'success' : 
                                                      competitor.rank <= 10 ? 'warning' : 'error'
                                                    }
                                                    variant="filled"
                                                    sx={{ fontSize: '0.6rem', height: 20 }}
                                                  />
                                                </TableCell>
                                                <TableCell sx={{ fontSize: '0.75rem' }}>
                                                  {competitor.placeId || 'N/A'}
                                                </TableCell>
                                                <TableCell sx={{ fontSize: '0.75rem' }}>
                                                  {competitor.rating ? (
                                                    <Box display="flex" alignItems="center" gap={0.5}>
                                                      <span>⭐ {competitor.rating}</span>
                                                    </Box>
                                                  ) : (
                                                    'N/A'
                                                  )}
                                                </TableCell>
                                                 <TableCell sx={{ fontSize: '0.75rem' }}>
                                                  {competitor.reviewCount
 || 'N/A'}
                                                </TableCell>
                                              </TableRow>
                                            )})}
                                          </TableBody>
                                        </Table>
                                      ) : (
                                        <Alert severity="info" sx={{ py: 0, mt: 1 }}>
                                          <Typography variant="caption">
                                            Keine Wettbewerber-Daten verfügbar für diesen Standort.
                                          </Typography>
                                        </Alert>
                                      )}
                                    </Box>
                                  </Collapse>
                                </TableCell>
                              </TableRow>
                            </React.Fragment>
                          ))}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  ) : (
                    <Box sx={{ p: 2 }}>
                      <Alert severity="info">
                        Keine Wettbewerber-Daten verfügbar für dieses Keyword.
                      </Alert>
                    </Box>
                  )}
                  
                  <Box sx={{ p: 1.5, borderTop: '1px solid #e0e0e0' }}>
                    <Typography variant="caption" color="textSecondary">
                      💡 Klicken Sie auf die Pfeile, um detaillierte Informationen zu jedem Wettbewerber an jedem Standort zu sehen.
                    </Typography>
                  </Box>
                </Box>
              </Collapse>
            </Box>
          </Box>
        )}
      </Box>
    </Box>
  );
};

export default LocationMap;